import React from 'react';
import { toNumber } from 'lodash';
import { withRouter, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

import ImageSliderStandard from 'components/consumer/image-slider-standard';
// import getBreakpoint from 'commons/getBreakpoint';
import ProductPageSectionTitle from 'components/consumer/ProductPageSectionTitle/ProductPageSectionTitle';
import CtaLink from 'components/essentials/Cta/CtaLink';
import MobileAndTabletOnly from 'components/consumer/MobileAndTabletOnly';
import DesktopOnly from 'components/consumer/DesktopOnly';
import useSuspenseQuery from 'commons/useSuspenseQuery';
import SuspenseWithLoader from 'components/consumer/SuspenseWithLoader';
import ReviewCard from './ReviewCard';
import testimonials from './testimonials';
import { GET_PRODUCT_REVIEWS } from './queries';

const DEFAULT_REVIEWS = [];

const SlickButtonFix = ({ currentSlide, slideCount, children, ...props }) => (
  <span {...props}>{children}</span>
);

SlickButtonFix.propTypes = {
  children: PropTypes.node,
  currentSlide: PropTypes.shape({
    src: PropTypes.string,
    url: PropTypes.string,
    value: PropTypes.string,
  }),
  slideCount: PropTypes.number,
};

const Testimonial = item => (
  <div className="text-base text-gray mr-10 max-w-[70%] min-w-[240px] [&_p]:font-normal">
    <div className="h-10 flex items-center justify-start mx-auto my-0 lg:justify-center">
      <img src={item.image} alt="" />
    </div>
    <p>{item.text}</p>
  </div>
);

const CustomerReviews = ({
  fromModularLink = '',
  staticContext,
  product,
  match,
  productName,
  id,
}) => {
  const { slug } = useParams();
  const { data } = useSuspenseQuery(GET_PRODUCT_REVIEWS, {
    variables: {
      id,
      slug,
      sortBy: 'date-desc',
      offset: 0,
      limit: 12,
    },
    fetchPolicy: 'cache-and-network',
  });
  const reviews = data?.productReviews?.reviews ?? DEFAULT_REVIEWS;

  if (!data) return null;

  const { productReviews } = data;
  const averageScore = productReviews.averageRating ?? 0;

  const reviewsList = reviews?.length
    ? reviews.map(item => (
        <ReviewCard
          productReview={item}
          key={item.id}
          staticContext={staticContext}
        />
      ))
    : null;

  const finalCTALink = product
    ? fromModularLink || `/${match.params.category}/${product?.slug}/`
    : '';

  return (
    <SuspenseWithLoader
      fallback={
        <div className="text-lg font-bold flex items-center justify-center">
          Loading..
        </div>
      }
    >
      {reviews?.length > 0 && (
        <div data-xc="customer-product-reviews" className="reviews">
          <div className="p-0 mb-0">
            <MobileAndTabletOnly>
              <div className="lg:hidden text-base pb-2 font-bold text-gray">
                <span>
                  {toNumber(averageScore).toFixed(1)} / 5 (
                  {productReviews.totalReviews || 0} Reviews)
                </span>
              </div>
            </MobileAndTabletOnly>

            <ProductPageSectionTitle className="text-2xl flex items-center font-bold [&_span]:text-base [&_span]:text-gray [&_span]:mt-[.4rem] [&_span]:ml-4">
              Customers love the {product ? product.name : productName || ''}
              <DesktopOnly>
                <span className="max-[1023px]:hidden">
                  {toNumber(averageScore).toFixed(1)} / 5 (
                  {productReviews.totalReviews || 0} Reviews)
                </span>
              </DesktopOnly>
            </ProductPageSectionTitle>
            {product?.name && (
              <p className="text-base mt-0 mx-0 mb-4 hidden lg:block">
                Read real customer reviews and what they’re saying about this
                product.
              </p>
            )}
          </div>
          <div className="pb-8 lg:pt-[.8rem] lg:px-0 lg:pb-8">
            <div className="max-w-full flex-[1_1] block min-h-0 min-w-0 mt-4 mx-0 mb-8 lg:mt-0 lg:mx-0 lg:mb-4 lg:p-0">
              {reviewsList && (
                <ImageSliderStandard
                  config={{
                    children: reviewsList,
                    sliderConfig: {
                      navType: 'Button',
                      visibleSlides: 3,
                      imageHeight: 430,
                    },
                    variation: { value: 'Component' },
                  }}
                  fullWidth
                  variant="reviews-list"
                />
              )}
            </div>
            {product?.slug && (
              <CtaLink
                to={`${finalCTALink}reviews`}
                styling="outline-button"
                color="brand"
              >
                See all{' '}
                {productReviews.totalReviews ? productReviews.totalReviews : 0}{' '}
                Reviews
              </CtaLink>
            )}
          </div>

          <div className="flex [border-top:2px_solid_#f8f8f8] overflow-auto pt-8 flex-nowrap pb-0 lg:mt-4 lg:pt-16 lg:pb-0 [&_div]:text-sm [&_div]:flex-[1_1] [&_div]:text-gray [&_div]:mr-8 [&_div]:w-[280px] [&_div:last-child]:last:mr-0 [&_div]:lg:text-center [&_div]:lg:w-auto [&_div]:lg:mr-16 [&_div_p]:font-normal [&_div_p]:mb-0 [&_img]:mb-4">
            {testimonials.map(item => (
              <Testimonial {...item} key={item.image} />
            ))}
          </div>
        </div>
      )}
    </SuspenseWithLoader>
  );
};

export default withRouter(CustomerReviews);
